<template>
  <div class="photos-view">
    <div v-if="keyword" class="search-result-header">
      <h2>关键字"{{ keyword }}"搜索的结果</h2>
      <button class="clear-search" @click="clearSearch">
        <i class="fas fa-times"></i>
        清除搜索
      </button>
    </div>

    <div v-else class="filters-container">
      <div class="filter-section">
        <h3 class="filter-title">图片标签</h3>
        <div class="categories-wrapper">
          <div class="category-tag reset-tag" :class="{ active: selectedCategory === null }" @click="resetCategory">
            <i class="fas fa-sync-alt"></i>
            全部标签
          </div>
          <div v-for="category in categories" :key="category.id" class="category-tag"
            :class="{ active: selectedCategory === category.id }" @click="selectCategory(category.id)">
            <!-- <i :class="category.icon"></i> -->
            {{ category.name }}
          </div>
        </div>
      </div>

      <div class="filter-section uploader-section">
        <h3 class="filter-title">上传者</h3>
        <div class="uploader-filter">
          <div class="search-input-wrapper">
            <i class="fas fa-user search-icon"></i>
            <input type="text" v-model="uploaderName" placeholder="输入上传者名称搜索" @keyup.enter="handleUploaderFilter">
            <button v-if="uploaderName" @click="clearUploaderFilter" class="clear-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <button class="search-btn" @click="handleUploaderFilter">
            搜索
          </button>
        </div>
      </div>

      <div class="filter-section sort-section">
        <h3 class="filter-title">排序方式</h3>
        <div class="sort-wrapper">
          <div class="sort-control">
            <i class="fas fa-sort sort-icon"></i>
            <select v-model="sortParams" @change="handleSortChange" class="sort-select">
              <option value="create_time,false">最新优先</option>
              <option value="create_time,true">最旧优先</option>
              <option value="view_count,false">浏览量从高到低</option>
              <option value="view_count,true">浏览量从低到高</option>
            </select>
            <i class="fas fa-chevron-down dropdown-icon"></i>
          </div>
        </div>
      </div>
    </div>

    <div class="filter-section">
      <div class="photos-header">
        <h2 v-if="!keyword">{{ selectedCategory ? getCategoryName(selectedCategory) : '全部图片' }}</h2>
        <div class="view-options">
          <button class="view-btn" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'">
            <i class="fas fa-th-large"></i>
          </button>
          <button class="view-btn" :class="{ active: viewMode === 'masonry' }" @click="viewMode = 'masonry'">
            <i class="fas fa-th"></i>
          </button>
        </div>
      </div>

      <div class="photos-grid" :class="viewMode">
        <div v-for="photo in filteredPhotos" :key="photo.imageId" class="photo-item">
          <div class="photo-actions">
            <button class="favorite-btn" :class="{ 'is-favorited': isFavorited(photo.imageId) }"
              @click.stop="toggleFavorite(photo.imageId)">
              <i class="fas" :class="isFavorited(photo.imageId) ? 'fa-heart' : 'fa-regular fa-heart'"></i>
            </button>
          </div>

          <div class="photo-wrapper" @click="viewPhotoDetail(photo.imageId)">
            <img :src="photo.imageUrl" :alt="photo.imageName">
            <div class="three-d-badge" v-if="photo.threeDModelUrl">
              <i class="fas fa-cube"></i> 3D
            </div>
          </div>

          <div class="photo-info" @click="viewPhotoDetail(photo.imageId)">
            <h3>{{ photo.imageName }}</h3>
            <div class="photo-meta">
              <span><i class="fas fa-eye"></i> {{ photo.viewCount }}</span>
            </div>
          </div>
        </div>
        <div v-if="loading" class="loading-more">
          <i class="fas fa-spinner fa-spin"></i>
          加载更多...
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request } from '@/utils/request'
import { favoritesMixin } from '@/mixins/favoritesMixin'

export default {
  name: 'PhotosView',
  mixins: [favoritesMixin],
  data() {
    return {
      selectedCategory: null,
      viewMode: 'grid',
      categories: [], // 移除静态导入
      photos: [],
      page: 1,
      loading: false,
      hasMore: true,
      keyword: '',
      uploaderName: '',
      sortParams: 'create_time,false',
    }
  },
  async created() {
    await this.loadTags() // 新增标签加载
    this.keyword = this.$route.query.keyword || ''
    this.loadPhotos()
    this.setupInfiniteScroll()
    this.loadFavorites()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  watch: {
    '$route.query.keyword'(newKeyword) {
      this.keyword = newKeyword || ''
      this.page = 1
      this.photos = []
      this.loadPhotos()
    }
  },
  computed: {
    filteredPhotos() {
      return this.photos;
    }
  },
  methods: {
    // 新增标签获取方法
    async loadTags() {
      try {
        const data = await request('/api/tag/list')
        this.categories = data.data
          .sort((a, b) => a.sort - b.sort)
          .map(item => ({
            id: item.id,
            name: item.name
          }))
      } catch (error) {
        console.error('获取标签失败:', error)
      }
    },
    resetCategory() {
      this.selectedCategory = null
      this.page = 1
      this.photos = []
      this.loadPhotos()
    },
    selectCategory(categoryId) {
      this.selectedCategory = this.selectedCategory === categoryId ? null : categoryId
      this.page = 1
      this.photos = []
      this.loadPhotos()
    },
    getCategoryName(categoryId) {
      const category = this.categories.find(c => c.id === categoryId)
      return category ? category.name : ''
    },
    setupInfiniteScroll() {
      window.addEventListener('scroll', this.handleScroll)
    },
    handleScroll() {
      if (this.loading || !this.hasMore) return

      const scrollHeight = document.documentElement.scrollHeight
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const clientHeight = document.documentElement.clientHeight

      if (scrollHeight - scrollTop - clientHeight < 200) {
        this.loadMorePhotos()
      }
    },
    // 添加排序变更处理方法
    handleSortChange() {
      this.page = 1
      this.photos = []
      this.loadPhotos()
    },

    async loadPhotos() {
      const [sortField, ascending] = this.sortParams.split(',')

      this.loading = true
      try {
        const params = {
          pageNum: this.page,
          pageSize: 10,
          sortField,
          ascending: ascending === 'true'
        }

        if (this.keyword) {
          params.keyword = this.keyword
        } else if (this.selectedCategory) {
          const category = this.categories.find(c => c.id === this.selectedCategory)
          if (category) {
            params.tag = category.name
          }
        }

        if (this.uploaderName.trim()) {
          params.uploaderName = this.uploaderName.trim()
        }

        const data = await request('/api/image/page/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(params)
        })

        if (data) {
          const records = data.data.list
          this.photos = this.page === 1 ? records : [...this.photos, ...records]
          this.hasMore = data.data.pageNum < data.data.pages
        }
      } catch (error) {
        console.error('加载图片失败:', error)
      }
      this.loading = false
    },
    async loadMorePhotos() {
      if (this.loading || !this.hasMore) return

      this.loading = true
      this.page += 1

      // 添加排序参数
      const [sortField, ascending] = this.sortParams.split(',')

      try {
        const params = {
          pageNum: this.page,
          pageSize: 10,
          sortField,
          ascending: ascending === 'true'
        }

        if (this.keyword) {
          params.keyword = this.keyword
        } else if (this.selectedCategory) {
          const category = this.categories.find(c => c.id === this.selectedCategory)
          if (category) {
            params.tag = category.name
          }
        }

        if (this.uploaderName.trim()) {
          params.uploaderName = this.uploaderName.trim()
        }

        const data = await request('/api/image/page/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(params)
        })

        if (data) {
          const records = data.data.list
          this.photos = [...this.photos, ...records]
          this.hasMore = data.data.pageNum < data.data.pages
        }
      } catch (error) {
        console.error('加载更多图片失败:', error)
        this.page -= 1
      }

      this.loading = false
    },
    viewPhotoDetail(id) {
      // this.$router.push(`/photos/${id}`)
      window.open(`/#/photos/${id}`, '_blank')
    },
    clearSearch() {
      this.$router.push('/photos')
    },
    handleUploaderFilter() {
      this.page = 1
      this.photos = []
      this.loadPhotos()
    },
    clearUploaderFilter() {
      this.uploaderName = ''
      this.page = 1
      this.photos = []
      this.loadPhotos()
    }
  }
}
</script>

<style scoped>
.photos-view {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 20px;
  /* 为导航栏留出最小空间 */
}

.filters-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 24px;
  margin-bottom: 30px;
  position: relative;
  /* 确保阴影不被遮挡 */
  z-index: 1;
  /* 确保在正确的层级 */
}

.filter-title {
  font-size: 18px;
  color: #333;
  margin-bottom: 16px;
  font-weight: 600;
}

.categories-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.category-tag {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: #f5f5f5;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  color: #666;
  border: 1px solid transparent;
}

.reset-tag {
  background: #e8f5e9;
  color: #4CAF50;
  border: 1px solid #4CAF50;
}

.reset-tag:hover {
  background: #4CAF50;
  color: white;
}

.reset-tag.active {
  background: #4CAF50;
  color: white;
}

.category-tag i {
  margin-right: 8px;
  font-size: 14px;
}

.category-tag .count {
  margin-left: 6px;
  font-size: 12px;
  color: #999;
}

.category-tag:hover {
  background: #eef7ee;
  color: #4CAF50;
}

.category-tag.active {
  background: #4CAF50;
  color: white;
  border-color: #4CAF50;
}

.category-tag.active .count {
  color: rgba(255, 255, 255, 0.8);
}

.photos-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.photos-header h2 {
  font-size: 24px;
  color: #333;
  font-weight: 600;
}

.view-options {
  display: flex;
  gap: 8px;
}

.view-btn {
  padding: 8px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  transition: all 0.3s ease;
}

.view-btn:hover {
  border-color: #4CAF50;
  color: #4CAF50;
}

.view-btn.active {
  background: #4CAF50;
  border-color: #4CAF50;
  color: white;
}

.photos-grid {
  display: grid;
  gap: 24px;
}

.photos-grid.grid {
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
}

.photos-grid.masonry {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 30px;
}

.photo-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.photo-item:hover {
  transform: translateY(-5px);
}

.photo-wrapper {
  position: relative;
  width: 100%;
  padding-top: 75%;
  overflow: hidden;
  background: #f5f5f5;
  cursor: pointer;
}

.photo-wrapper img {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.photo-item:hover .photo-actions {
  opacity: 1;
}

.favorite-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: all 0.3s ease;
}

.favorite-btn:hover {
  background: white;
  color: #f44336;
}

.favorite-btn.is-favorited {
  color: #f44336;
}

.favorite-btn i {
  font-size: 18px;
}

.photo-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  cursor: pointer;
}

.photo-info h3 {
  margin: 0 0 8px;
  font-size: 16px;
}

.photo-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  opacity: 0.9;
}

.photo-meta span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.three-d-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(33, 150, 243, 0.9);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  z-index: 2;
}

@media (max-width: 768px) {
  .photos-view {
    padding: 16px;
    margin-top: 10px;
  }

  .filters-container {
    padding: 16px;
  }

  .photos-grid.grid,
  .photos-grid.masonry {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
  }
}

.loading-more {
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.loading-more i {
  font-size: 16px;
  color: #4CAF50;
}

.search-result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.search-result-header h2 {
  font-size: 20px;
  color: #333;
  margin: 0;
}

.clear-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
}

.clear-search:hover {
  border-color: #f44336;
  color: #f44336;
}

.uploader-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eee;
}

.uploader-filter {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  max-width: 500px;
  /* 限制输入框的最大宽度 */
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #999;
  font-size: 14px;
}

.uploader-filter input {
  flex: 1;
  width: 100%;
  padding: 10px 36px 10px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
  background: #f5f5f5;
}

.uploader-filter input:focus {
  border-color: #4CAF50;
  background: white;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.uploader-filter input::placeholder {
  color: #999;
}

.clear-btn {
  position: absolute;
  right: 8px;
  padding: 6px;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-radius: 50%;
}

.clear-btn:hover {
  color: #f44336;
  background: rgba(244, 67, 54, 0.1);
}

.search-btn {
  padding: 10px 24px;
  background: #4CAF50;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.search-btn:hover {
  background: #43A047;
  transform: translateY(-1px);
}

.search-btn:active {
  transform: translateY(0);
}

.sort-section {
  margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #eee;
}

@media (max-width: 768px) {
  .uploader-section {
    margin-top: 20px;
    padding-top: 20px;
  }

  .uploader-filter {
    max-width: 100%;
    /* 移动端下占满宽度 */
    gap: 8px;
  }

  .uploader-filter input {
    padding: 8px 32px 8px 32px;
    font-size: 13px;
  }

  .search-btn {
    padding: 8px 16px;
    font-size: 13px;
  }

  .search-icon {
    left: 10px;
    font-size: 13px;
  }
}
.sort-wrapper {
  display: flex;
  align-items: center;
  margin-top: 10px;
}

.sort-control {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 0 8px;
  transition: all 0.3s ease;
}

.sort-control:hover {
  border-color: #4CAF50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.sort-icon {
  color: #666;
  margin-right: 8px;
  font-size: 14px;
}

.sort-select {
  appearance: none;
  background: transparent;
  border: none;
  padding: 10px 30px 10px 0;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  outline: none;
  width: 100%;
}

.dropdown-icon {
  position: absolute;
  right: 10px;
  color: #666;
  pointer-events: none;
  transition: transform 0.3s ease;
}

.sort-control:hover .dropdown-icon {
  color: #4CAF50;
}

.sort-select:focus + .dropdown-icon {
  transform: rotate(180deg);
}

@media (max-width: 768px) {
  .sort-wrapper {
    margin-top: 8px;
  }

  .sort-control {
    width: 100%;
  }

  .sort-select {
    padding: 8px 30px 8px 0;
    font-size: 13px;
  }
}
</style>
